import React, { useState, useEffect, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: "AIzaSyCs4ynwRb8q-ftaWnGaBCdWZT1kunIMu90",
  authDomain: "actmobile-dda9d.firebaseapp.com",
  databaseURL: "https://actmobile-dda9d.firebaseio.com",
  projectId: "actmobile-dda9d",
  storageBucket: "actmobile-dda9d.firebasestorage.app",
  messagingSenderId: "728762119993",
  appId: "1:728762119993:web:cce4ae0bd14e1d21cb1ae8",
  measurementId: "G-JFP5KPRZH1"
};

const VAPID_KEY = 'BLZGG7AHl_aM46vcfmCeXr69Nv-tNuT5bqllDcmzuJwgCXEoH4H3u8VXGvz8M3BWElXyZnvvkyivpn8dKw7HOWg';
const FCM_TOKEN_STORAGE_KEY = 'fcm_token';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const getPlatformInfo = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /android/i.test(ua);
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  const isChrome = /chrome|chromium|crios/i.test(ua);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  const isSimulator = window.navigator.userAgent.includes('iPhone Simulator') || 
                      window.navigator.userAgent.includes('iPad Simulator');
  
  let iOSVersion = null;
  if (isIOS) {
    const match = navigator.userAgent.match(/OS (\d+)_?(\d+)?/);
    if (match) {
      iOSVersion = {
        major: parseInt(match[1], 10),
        minor: match[2] ? parseInt(match[2], 10) : 0
      };
    }
  }
  
  return {
    isIOS,
    isAndroid,
    isSafari,
    isChrome,
    isMobile: isIOS || isAndroid,
    isDesktop: !(isIOS || isAndroid),
    isStandalone,
    isSimulator,
    iOSVersion
  };
};

const checkBrowserSupport = async () => {
  const platform = getPlatformInfo();
  const hasNotification = 'Notification' in window;
  const hasServiceWorker = 'serviceWorker' in navigator;
  const hasPushManager = 'PushManager' in window;
  const isSecure = window.isSecureContext;
  let unsupportedReason = '';
  let iosWebPushSupported = false;
  
  // Check if Firebase messaging is supported in this environment
  let firebaseMessagingSupported = false;
  try {
    firebaseMessagingSupported = await isSupported();
  } catch (err) {
    console.log('Error checking Firebase messaging support:', err);
  }

  // For iOS, check for iOS 16.4+ for web push support
  if (platform.isIOS && platform.iOSVersion) {
    if (platform.iOSVersion.major > 16 || (platform.iOSVersion.major === 16 && platform.iOSVersion.minor >= 4)) {
      iosWebPushSupported = true;
    }
  }
  
  // Check for simulator - simulator doesn't fully support notifications
  if (platform.isSimulator) {
    unsupportedReason = 'iOS Simulator environment detected. Web push notifications are not fully supported in the simulator. Please test on a real device.';
    return {
      hasBasicSupport: false,
      platformSupport: {
        ios: false,
        android: false,
        desktop: false
      },
      platform,
      unsupportedReason,
      firebaseMessagingSupported
    };
  }

  if (!hasNotification) {
    unsupportedReason = 'Your browser does not support web notifications.';
  } else if (!hasServiceWorker) {
    unsupportedReason = 'Your browser does not support service workers, which are required for notifications.';
  } else if (!hasPushManager) {
    unsupportedReason = 'Your browser does not support the Push API, which is required for notifications.';
  } else if (!isSecure) {
    unsupportedReason = 'Notifications require a secure connection (HTTPS).';
  } else if (platform.isIOS && !iosWebPushSupported) {
    unsupportedReason = 'iOS requires version 16.4 or later for web push notifications. Please update your iOS or add this website to your home screen for the best experience.';
  } else if (!firebaseMessagingSupported) {
    unsupportedReason = 'Firebase Messaging is not supported in this browser environment.';
  }

  return {
    hasBasicSupport: hasNotification && hasServiceWorker && hasPushManager && isSecure && firebaseMessagingSupported,
    platformSupport: {
      ios: platform.isIOS ? (iosWebPushSupported && firebaseMessagingSupported) : false,
      android: platform.isAndroid && hasNotification && hasServiceWorker && hasPushManager && firebaseMessagingSupported,
      desktop: platform.isDesktop && hasNotification && hasServiceWorker && hasPushManager && firebaseMessagingSupported
    },
    platform,
    unsupportedReason,
    firebaseMessagingSupported
  };
};

const PushNotificationComponent = () => {
  const [token, setToken] = useState('');
  const [notificationMessage, setNotificationMessage] = useState(null);
  const [error, setError] = useState('');
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);
  const [supportInfo, setSupportInfo] = useState(null);
  const [messaging, setMessaging] = useState(null);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [instructionsExpanded, setInstructionsExpanded] = useState(false);
  const [fallbackMode, setFallbackMode] = useState(false);


  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize Firebase, messaging, and load support info/history
  useEffect(() => {
    const initializeNotifications = async () => {
      try {
        const support = await checkBrowserSupport();
        setSupportInfo(support);
        
        // If in iOS simulator, use fallback mode
        if (support.platform.isSimulator) {
          console.log('iOS Simulator detected - using fallback notification mode');
          setFallbackMode(true);
          setError(support.unsupportedReason);
          loadNotificationHistory();
          return;
        }

        // Guard against environments where Notification is not supported.
        if (!("Notification" in window)) {
          setError('Your browser does not support web notifications.');
          setFallbackMode(true);
          return;
        }

        // If Firebase Messaging isn't supported, use fallback
        if (!support.firebaseMessagingSupported) {
          console.log('Firebase Messaging not supported - using fallback notification mode');
          setFallbackMode(true);
          setError(support.unsupportedReason || 'Firebase Messaging is not supported in this browser.');
          loadNotificationHistory();
          return;
        }

        // For iOS in standalone mode, we need special handling
        const isIOSStandalone = support.platform.isIOS && support.platform.isStandalone;
        
        try {
          // Initialize Firebase
          const app = initializeApp(firebaseConfig);
          
          // Even if basic support isn't available, initialize for iOS standalone mode
          if (support.platformSupport.android || support.platformSupport.desktop || 
              support.platformSupport.ios || isIOSStandalone) {
            const msgInstance = getMessaging(app);
            setMessaging(msgInstance);
            
            // Use a safe check for Notification API
            const permission = window.Notification ? window.Notification.permission : 'default';
            setIsPermissionGranted(permission === 'granted');

            if (permission === 'granted') {
              // Consolidated token retrieval & storage logic
              fetchAndStoreToken(msgInstance);
              loadNotificationHistory();
            }
          } else {
            setFallbackMode(true);
          }
        } catch (err) {
          console.error("Initialization error:", err);
          setError(`Initialization error: ${err.message}`);
          setFallbackMode(true);
        }
      } catch (err) {
        console.error("Support check error:", err);
        setError(`Support check error: ${err.message}`);
        setFallbackMode(true);
      }
    };

    initializeNotifications();
  }, []);

  const fetchAndStoreToken = async (messagingInstance) => {
    // Check if token already exists in localStorage
    const storedToken = localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
    console.log("Stored token:", storedToken);
    if (storedToken) {
      setToken(storedToken);
      return;
    }
    
    try {
      // Check if service worker API is available
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        throw new Error("Push notifications not supported in this browser");
      }

      // First, try to get the existing service worker registration
      let messagingSWRegistration = null;
      
      try {
        // Register service worker with better error handling
        try {
          if ('serviceWorker' in navigator) {
            // First check if we have an existing registration
            const registrations = await navigator.serviceWorker.getRegistrations();
            let existingRegistration = null;
            
            for (const reg of registrations) {
              if (reg.active && reg.active.scriptURL.includes('firebase-messaging-sw.js')) {
                existingRegistration = reg;
                break;
              }
            }
            
            if (existingRegistration) {
              console.log('Using existing firebase-messaging-sw.js registration');
              messagingSWRegistration = existingRegistration;
            } else {
              console.log('Registering new firebase-messaging-sw.js');
              messagingSWRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
                scope: '/'
              });
              
              // Wait for the service worker to be ready
              if (messagingSWRegistration.installing) {
                console.log('Service worker is installing');
                await new Promise((resolve) => {
                  messagingSWRegistration.installing.addEventListener('statechange', (e) => {
                    if (e.target.state === 'activated') {
                      console.log('Service worker activated');
                      resolve();
                    }
                  });
                });
              }
            }
          } else {
            throw new Error('Service Worker API not available');
          }
        } catch (regError) {
          console.error("Service worker registration error:", regError);
          throw regError;
        }
        
        // Now try to get token with the registration
        try {
          if (!messagingSWRegistration) {
            throw new Error('No valid service worker registration');
          }
          
          // Check for existing push subscription
          let pushSubscription = await messagingSWRegistration.pushManager.getSubscription();
          
          if (!pushSubscription) {
            console.log('Creating new push subscription');
            try {
              // Convert VAPID key to Uint8Array with improved function
const applicationServerKey = urlBase64ToUint8Array(VAPID_KEY);

// Create a subscription with the PushManager with better error handling
try {
  pushSubscription = await messagingSWRegistration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: applicationServerKey
  });
  
  // Verify the subscription has an endpoint
  if (!pushSubscription.endpoint) {
    throw new Error("Created push subscription has no endpoint");
  }
  
  console.log("Push subscription created successfully");
  console.log("Endpoint:", pushSubscription.endpoint.substring(0, 50) + "...");
  
  // Wait a moment for the subscription to be fully established
  await new Promise(resolve => setTimeout(resolve, 1000));
} catch (subscriptionError) {
  console.error("Detailed push subscription error:", subscriptionError);
  
  if (subscriptionError.name === 'NotAllowedError') {
    throw new Error("Permission denied for push subscription");
  } else if (subscriptionError.name === 'InvalidStateError') {
    throw new Error("Service worker not activated");
  }
  
  throw new Error(`Failed to create push subscription: ${subscriptionError.message}`);
}
            } catch (subscriptionError) {
              console.error('Push subscription error:', subscriptionError);
              throw new Error(`Push subscription failed: ${subscriptionError.message}`);
            }
          }
          
          // Get Firebase token
          console.log('Getting Firebase token');
          const currentToken = await getToken(messagingInstance, {
            vapidKey: VAPID_KEY,
            serviceWorkerRegistration: messagingSWRegistration
          });
          
          if (currentToken) {
            console.log('Token obtained successfully');
            setToken(currentToken);
            localStorage.setItem(FCM_TOKEN_STORAGE_KEY, currentToken);
            
            const notification = {
              title: 'Notifications Enabled',
              body: 'You will now receive push notifications from this app.',
              timestamp: new Date().toLocaleTimeString(),
              type: 'success',
              id: Date.now()
            };
            setNotificationMessage(notification);
            updateNotificationHistory(notification);
          } else {
            console.error('No token returned');
            throw new Error('No registration token available from Firebase.');
          }
        } catch (tokenErr) {
          console.error('Token error:', tokenErr);
          
          // Special error handling for iOS
          if (supportInfo?.platform?.isIOS) {
            handleIOSFallback(tokenErr);
          } else {
            setError(`Token error: ${tokenErr.message}`);
            setShowInstructions(true);
          }
        }
      } catch (swErr) {
        console.error('Service worker error:', swErr);
        
        if (supportInfo?.platform?.isIOS) {
          handleIOSFallback(swErr);
        } else {
          setError(`Service worker error: ${swErr.message}`);
          setShowInstructions(true);
        }
      }
    } catch (err) {
      console.error('General error:', err);
      setError(`Error: ${err.message}`);
      setShowInstructions(true);
      setFallbackMode(true);
    }
  };

  const handleIOSFallback = (error) => {
    console.log('Switching to iOS fallback notification mode');
    setFallbackMode(true);
    setError(`iOS notification error: ${error.message}. Using in-app notifications instead.`);
    
    const notification = {
      title: 'In-App Notifications Active',
      body: 'Web push is not fully supported in this environment. We\'ll use in-app notifications instead.',
      timestamp: new Date().toLocaleTimeString(),
      type: 'info',
      id: Date.now()
    };
    setNotificationMessage(notification);
    updateNotificationHistory(notification);
  };

  const loadNotificationHistory = () => {
    try {
      const history = JSON.parse(localStorage.getItem('notification_history')) || [];
      setNotificationHistory(history);
    } catch (err) {
      console.error("Error loading notification history:", err);
    }
  };

  const updateNotificationHistory = useCallback((notification) => {
    setNotificationHistory(prevHistory => {
      const updated = [notification, ...prevHistory].slice(0, 10);
      localStorage.setItem('notification_history', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Simplified request permission function with better iOS handling
  const requestPermissionAndFetchToken = async () => {
    try {
      // If we're in fallback mode, just show in-app notifications
      if (fallbackMode) {
        handleAlternativeNotificationOption();
        return;
      }
      
      console.log('Requesting notification permission');
      const permission = await Notification.requestPermission();
      console.log('Permission response:', permission);
      setIsPermissionGranted(permission === 'granted');
      
      if (permission === 'granted') {
        // For iOS we need to make sure we send a test notification
        if (supportInfo?.platform?.isIOS) {
          const iosTestNotification = new Notification('Notifications Enabled', {
            body: 'Push notifications are now enabled for this app.',
            icon: '/logo192.png'
          });
          
          // Close the test notification after 3 seconds
          setTimeout(() => {
            iosTestNotification.close();
          }, 3000);
        }
        
        await fetchAndStoreToken(messaging);
      } else {
        setError('Notification permission denied.');
        setShowInstructions(true);
        setInstructionsExpanded(true);
      }
    } catch (err) {
      console.error('Permission request error:', err);
      setError(`Permission error: ${err.message}`);
      setShowInstructions(true);
      setFallbackMode(true);
    }
  };

  const handleAlternativeNotificationOption = () => {
    const { platform } = supportInfo || {};
    let message = 'Your browser doesn\'t support push notifications. We\'ll use in-app notifications instead.';
    
    if (platform?.isIOS) {
      if (platform?.isStandalone) {
        message = 'This app needs notification permission. Please check your iOS Settings > Notifications to enable.';
      } else if (platform?.isSimulator) {
        message = 'iOS Simulator does not fully support web push notifications. Using in-app notifications instead.';
      } else {
        message = 'For notifications, add this website to your home screen by tapping the share icon and selecting "Add to Home Screen".';
      }
    }
    
    const notification = {
      title: platform?.isIOS ? 'iOS Notifications Info' : 'In-App Notifications',
      body: message,
      timestamp: new Date().toLocaleTimeString(),
      type: 'info',
      id: Date.now()
    };
    setNotificationMessage(notification);
    updateNotificationHistory(notification);
  };

  const playNotificationSound = () => {
    try {
      const audio = new Audio('/ding.mp3');
      audio.play().catch(err => console.error('Error playing sound:', err));
    } catch (err) {
      console.error('Sound playback error:', err);
    }
  };

  const toggleNotificationHistory = () => setShowHistory(prev => !prev);
  const toggleInstructions = () => setInstructionsExpanded(prev => !prev);

  // Foreground message listener
  useEffect(() => {
    if (!messaging || fallbackMode) return;
    
    const unsubscribe = onMessage(messaging, (payload) => {
      if (payload.notification) {
        playNotificationSound();
        const notification = {
          title: payload.notification.title,
          body: payload.notification.body,
          timestamp: new Date().toLocaleTimeString(),
          type: 'foreground',
          id: Date.now()
        };
        setNotificationMessage(notification);
        updateNotificationHistory(notification);
      }
    });
    return () => unsubscribe();
  }, [messaging, updateNotificationHistory, fallbackMode]);

  // Background message listener via Service Worker
  useEffect(() => {
    if (fallbackMode) return;
    
    const messageHandler = (event) => {
      if (event.data?.type === 'BACKGROUND_NOTIFICATION' && event.data.payload?.notification) {
        playNotificationSound();
        const notification = {
          title: event.data.payload.notification.title,
          body: event.data.payload.notification.body,
          timestamp: new Date().toLocaleTimeString(),
          type: 'background',
          id: Date.now()
        };
        setNotificationMessage(notification);
        updateNotificationHistory(notification);
      }
    };
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', messageHandler);
    }
    
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', messageHandler);
      }
    };
  }, [updateNotificationHistory, fallbackMode]);

  // Fallback notification system for testing or unsupported environments
  const sendTestNotification = () => {
    const testNotification = {
      title: 'Test Notification',
      body: 'This is a test notification. In production, these would come from your server.',
      timestamp: new Date().toLocaleTimeString(),
      type: fallbackMode ? 'fallback' : 'foreground',
      id: Date.now()
    };
    
    playNotificationSound();
    setNotificationMessage(testNotification);
    updateNotificationHistory(testNotification);
  };

  const getNotificationBackgroundColor = (type) => {
    switch(type) {
      case 'foreground': return '#e3f2fd';
      case 'background': return '#e8f5e9';
      case 'info': return '#fff3e0';
      case 'error': return '#ffebee';
      case 'success': return '#e8f5e9';
      case 'fallback': return '#f3e5f5';
      default: return '#f8f9fa';
    }
  };

  // Render iOS-specific instructions based on environment
  const renderNotificationInstructions = () => {
    const { platform } = supportInfo || {};
    
    // iOS Simulator specific instructions
    if (platform?.isSimulator) {
      return (
        <div style={styles.instructionsContainer}>
          <h4 style={styles.instructionTitle}>iOS Simulator Limitations</h4>
          <p>The iOS Simulator has limited support for web push notifications. For full testing:</p>
          <ol style={styles.instructionList}>
            <li>Test on a physical iOS device running iOS 16.4 or later</li>
            <li>Add the app to home screen on the physical device</li>
            <li>Open from the home screen and allow notifications</li>
          </ol>
          <p>For now, you can use the "Send Test Notification" button below to test the in-app notification UI.</p>
          <button onClick={sendTestNotification} style={styles.instructionButton}>
            Send Test Notification
          </button>
          <button onClick={() => setShowInstructions(false)} style={{...styles.instructionButton, marginLeft: '10px', backgroundColor: '#999'}}>
            Close
          </button>
        </div>
      );
    }
    
    // Other platform-specific instructions
    let instructionsContent = null;
    
    if (platform?.isIOS && platform?.isStandalone) {
      instructionsContent = (
        <>
          <h4 style={styles.instructionTitle}>Enable Notifications on iOS</h4>
          {instructionsExpanded && (
            <ol style={styles.instructionList}>
              <li>Open your device's Settings app</li>
              <li>Scroll down and tap on this app's name</li>
              <li>Tap "Notifications"</li>
              <li>Enable "Allow Notifications"</li>
              <li>Return to this app and refresh</li>
            </ol>
          )}
        </>
      );
    } else if (platform?.isIOS) {
      instructionsContent = (
        <>
          <h4 style={styles.instructionTitle}>Enable Notifications on iOS</h4>
          {instructionsExpanded && (
            <>
              <ol style={styles.instructionList}>
                <li>First, add this website to your home screen:</li>
                <li>Tap the share icon in Safari (square with arrow)</li>
                <li>Scroll down and tap "Add to Home Screen"</li>
                <li>Tap "Add" in the top right corner</li>
                <li>Open the app from your home screen</li>
                <li>When prompted, allow notifications</li>
              </ol>
              <p style={styles.instructionNote}>
                Note: iOS requires version 16.4 or later for web push notifications.
              </p>
            </>
          )}
        </>
      );
    } else {
      instructionsContent = (
        <>
          <h4 style={styles.instructionTitle}>Enable Notifications</h4>
          {instructionsExpanded && (
            <ol style={styles.instructionList}>
              <li>Check your browser settings</li>
              <li>Enable notifications for this website</li>
              <li>Reload the page</li>
            </ol>
          )}
        </>
      );
    }
    
    return (
      <div style={{ ...styles.instructionsContainer, ...(instructionsExpanded ? {} : styles.instructionsCollapsed) }}>
        <div style={styles.instructionsHeader} onClick={toggleInstructions}>
          {instructionsContent}
          <div style={styles.expandCollapseIcon}>
            {instructionsExpanded ? '▼' : '▶'}
          </div>
        </div>
        {instructionsExpanded && (
          <div style={styles.instructionActions}>
            {fallbackMode && (
              <button onClick={sendTestNotification} style={{...styles.instructionButton, marginRight: '10px', backgroundColor: '#4CAF50'}}>
                Send Test Notification
              </button>
            )}
            <button onClick={() => { setInstructionsExpanded(false); setShowInstructions(false); }} style={styles.instructionButton}>
              Got it
            </button>
          </div>
        )}
      </div>
    );
  };

  // Optimized notification button/icon
  const renderNotificationIcon = () => {
    const isSupported = supportInfo?.platformSupport.android || 
                        supportInfo?.platformSupport.desktop || 
                        supportInfo?.platformSupport.ios;
    
    let iconColor = "#FFFFFF";
    let statusTooltip = "";
    let statusIcon = null;
    let iconButtonStyle = styles.iconButton;
    
    if (fallbackMode) {
      statusTooltip = "In-app notifications only";
      iconButtonStyle = {
        ...styles.iconButton,
        backgroundColor: 'rgba(156, 39, 176, 0.3)',
        borderColor: '#9C27B0'
      };
      statusIcon = (
        <div style={styles.statusIcon} className="notification-status-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#9C27B0">
            <path d="M13 7h-2v4H7v2h4v4h2v-4h4v-2h-4z"/>
          </svg>
        </div>
      );
    } else if (isPermissionGranted && isSupported) {
      statusTooltip = "Notifications enabled";
      iconButtonStyle = {
        ...styles.iconButton,
        backgroundColor: 'rgba(76, 175, 80, 0.3)',
        borderColor: '#4CAF50'
      };
      statusIcon = (
        <div style={styles.statusIcon} className="notification-status-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#4CAF50">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
        </div>
      );
    } else if (isSupported) {
      iconColor = "#FFD700";
      iconButtonStyle = {
        ...styles.iconButton,
        backgroundColor: 'rgba(255, 215, 0, 0.3)',
        borderColor: '#FFD700'
      };
      statusTooltip = "Enable notifications";
    } else {
      statusTooltip = supportInfo?.unsupportedReason || "Notifications not available";
      iconButtonStyle = {
        ...styles.iconButton,
        backgroundColor: 'rgba(244, 67, 54, 0.3)',
        borderColor: '#F44336'
      };
      statusIcon = (
        <div style={styles.statusIcon} className="notification-status-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="#F44336">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </div>
      );
    }
    
    const handleIconClick = () => {
      if (fallbackMode) {
        toggleNotificationHistory();
      } else if (isSupported) {
        if (isPermissionGranted) {
          toggleNotificationHistory();
        } else {
          requestPermissionAndFetchToken();
        }
      } else {
        handleAlternativeNotificationOption();
      }
    };
    
    return (
      <div style={styles.iconContainer}>
        <button
          onClick={handleIconClick}
          style={{ ...iconButtonStyle, ...(isMobile ? styles.iconButtonMobile : {}) }}
          aria-label={statusTooltip}
          title={statusTooltip}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
            style={{ 
              ...styles.icon, 
              ...(isSupported && !isPermissionGranted && !fallbackMode ? styles.iconPulse : {})
            }}
            fill={iconColor}>
            <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"/>
          </svg>
          {statusIcon}
          {notificationHistory.length > 0 && (
            <span style={{ ...styles.badge, ...(isMobile ? styles.badgeMobile : {}) }}>
              {notificationHistory.length}
            </span>
          )}
        </button>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.headerSection}>
        {renderNotificationIcon()}
      </div>
      <div style={styles.contentSection}>
        {/* Display current mode and token info */}
        <div style={{ padding: '10px', background: '#eee', border: '1px solid #ccc', marginBottom: '10px', fontSize: '12px', wordBreak: 'break-all' }}>
          <strong>Mode:</strong> {fallbackMode ? 'In-App Notifications Only' : 'Web Push Notifications'}<br/>
          <strong>Token:</strong> {token ? token : 'N/A'}
        </div>
        
        {/* Extra controls for testing in fallback mode */}
        {fallbackMode && (
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
            <button onClick={sendTestNotification} style={styles.testButton}>
              Send Test Notification
            </button>
            <button onClick={toggleNotificationHistory} style={{...styles.testButton, backgroundColor: '#2196F3'}}>
              {showHistory ? 'Hide History' : 'Show History'}
            </button>
          </div>
        )}
        
        {/* Current notification */}
        {notificationMessage && (
          <div
            style={{
              ...styles.notificationInline,
              ...(isMobile ? styles.notificationInlineMobile : {}),
              backgroundColor: getNotificationBackgroundColor(notificationMessage.type)
            }}>
            <div style={styles.notificationContent}>
              <strong style={isMobile ? styles.mobileFontTitle : {}}>
                {notificationMessage.title}
              </strong>
              <p style={isMobile ? styles.mobileFontBody : {}}>
                {notificationMessage.body}
              </p>
            </div>
            <button
              style={{ ...styles.closeButton, ...(isMobile ? styles.closeButtonMobile : {}) }}
              onClick={() => setNotificationMessage(null)}
              aria-label="Close notification">
              ×
            </button>
          </div>
        )}
        
        {/* Instructions */}
        {showInstructions && renderNotificationInstructions()}
        
        {/* Notification history */}
        {showHistory && (
          <div style={{ ...styles.historyInline, ...(isMobile ? styles.historyInlineMobile : {}) }}>
            <div style={{ ...styles.historyHeader, ...(isMobile ? styles.historyHeaderMobile : {}) }}>
              <h3 style={{ ...styles.historyTitle, ...(isMobile ? styles.historyTitleMobile : {}) }}>
                Notifications
              </h3>
              <div style={styles.historyActions}>
                <button onClick={() => { setNotificationHistory([]); localStorage.removeItem('notification_history'); }}
                  style={{ ...styles.clearButton, ...(isMobile ? styles.clearButtonMobile : {}) }}
                  disabled={notificationHistory.length === 0}>
                  Clear All
                </button>
                <button onClick={toggleNotificationHistory}
                  style={{ ...styles.closeButton, ...(isMobile ? styles.closeButtonMobile : {}) }}
                  aria-label="Close history">
                  ×
                </button>
              </div>
            </div>
            <div style={styles.historyList}>
              {notificationHistory.length === 0 ? (
                <div style={styles.emptyHistory}>
                  <p style={isMobile ? styles.mobileFontBody : {}}>
                    No notifications yet
                  </p>
                </div>
              ) : (
                notificationHistory.map(notification => (
                  <div key={notification.id}
                    style={{
                      ...styles.historyItem,
                      ...(isMobile ? styles.historyItemMobile : {}),
                      backgroundColor: getNotificationBackgroundColor(notification.type)
                    }}>
                    <div style={styles.historyItemContent}>
                      <strong style={isMobile ? styles.mobileFontTitle : {}}>
                        {notification.title}
                      </strong>
                      <p style={isMobile ? styles.mobileFontBody : {}}>
                        {notification.body}
                      </p>
                      <span style={{ ...styles.historyMeta, ...(isMobile ? styles.historyMetaMobile : {}) }}>
                        {notification.timestamp}
                        {notification.type && (<span style={styles.notificationType}> • {notification.type}</span>)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        
        {/* Error message */}
        {error && !showInstructions && (
          <span style={{ ...styles.error, ...(isMobile ? styles.errorMobile : {}) }}>
            {error}
          </span>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: { position: 'relative', width: '100%', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif', boxSizing: 'border-box' },
  headerSection: { 
    position: 'fixed', 
    top: 0, 
    left: 0, 
    right: 10, 
    display: 'flex', 
    justifyContent: 'right', 
    alignItems: 'right',
    zIndex: 1000,
    height: '50px'
  },
  iconContainer: { 
    position: 'absolute', 
    zIndex: 1001,
    top: '5px'
  },
  iconButton: { 
    background: 'rgba(255, 255, 255, 0.2)', 
    border: '2px solid white', 
    borderRadius: '50%', 
    cursor: 'pointer', 
    width: '40px', 
    height: '40px', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    position: 'relative', 
    padding: '8px', 
    margin: '0',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.3)'
  },
  iconButtonMobile: { 
    width: '48px', 
    height: '48px', 
    padding: '12px', 
    touchAction: 'manipulation' 
  },
  icon: { 
    width: '24px', 
    height: '24px', 
    transition: 'all 0.3s ease',
    filter: 'drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.5))'
  },
  iconPulse: { 
    animation: 'pulse 2s infinite',
    filter: 'drop-shadow(0px 0px 4px white)'
  },
  statusIcon: { 
    position: 'absolute', 
    right: '-5px', 
    top: '-5px', 
    backgroundColor: 'white', 
    borderRadius: '50%', 
    width: '20px', 
    height: '20px', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    border: '1px solid #ccc'
  },
  '@keyframes pulse': {
    '0%': { transform: 'scale(1)', opacity: 1 },
    '50%': { transform: 'scale(1.1)', opacity: 0.7 },
    '100%': { transform: 'scale(1)', opacity: 1 }
  },
  badge: { 
    position: 'absolute', 
    bottom: '-5px', 
    right: '-5px', 
    backgroundColor: '#f44336', 
    color: 'white', 
    borderRadius: '50%', 
    width: '18px', 
    height: '18px', 
    fontSize: '12px', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    fontWeight: 'bold', 
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    border: '1px solid white'
  },
  badgeMobile: { 
    width: '22px', 
    height: '22px', 
    fontSize: '14px', 
    bottom: '-8px', 
    right: '-8px' 
  },
  contentSection: { width: '100%', marginTop: '62px', boxSizing: 'border-box', padding: '0 16px' },
  notificationInline: { backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '8px', padding: '16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', boxSizing: 'border-box', transition: 'background-color 0.2s ease', boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)' },
  notificationInlineMobile: { padding: '12px', borderRadius: '6px' },
  notificationContent: { flex: 1, wordBreak: 'break-word' },
  closeButton: { background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', padding: '0 4px', minWidth: '32px', minHeight: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' },
  closeButtonMobile: { minWidth: '44px', minHeight: '44px', fontSize: '1.75rem', touchAction: 'manipulation' },
  historyInline: { backgroundColor: 'white', border: '1px solid #e9ecef', borderRadius: '8px', width: '100%', maxHeight: '400px', display: 'flex', flexDirection: 'column', marginBottom: '16px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', boxSizing: 'border-box', overflow: 'hidden' },
  historyInlineMobile: { maxHeight: '60vh', borderRadius: '6px' },
  historyHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #eee', backgroundColor: '#f8f9fa', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' },
  historyHeaderMobile: { padding: '10px 12px' },
  historyTitle: { margin: 0, fontSize: '16px', fontWeight: '600' },
  historyTitleMobile: { fontSize: '18px' },
  historyActions: { display: 'flex', alignItems: 'center' },
  clearButton: { backgroundColor: 'transparent', border: 'none', color: '#666', fontSize: '12px', cursor: 'pointer', padding: '4px 8px', marginRight: '8px' },
  clearButtonMobile: { fontSize: '14px', padding: '8px 12px' },
  historyList: { overflowY: 'auto', maxHeight: 'calc(400px - 50px)', padding: '0', WebkitOverflowScrolling: 'touch' },
  historyItem: { padding: '12px 16px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'flex-start', transition: 'background-color 0.2s ease' },
  historyItemMobile: { padding: '10px 12px' },
  historyItemContent: { flex: 1 },
  historyMeta: { fontSize: '11px', color: '#999', display: 'block', marginTop: '4px' },
  notificationType: { fontStyle: 'italic' },
  historyMetaMobile: { fontSize: '12px' },
  emptyHistory: { padding: '20px 16px', textAlign: 'center', color: '#999' },
  error: { color: '#dc3545', margin: '8px 0 0', fontSize: '0.9rem', display: 'block', padding: '8px 12px', backgroundColor: '#ffebee', borderRadius: '4px', borderLeft: '3px solid #dc3545' },
  errorMobile: { fontSize: '1rem', padding: '10px 12px' },
  mobileFontTitle: { fontSize: '16px' },
  mobileFontBody: { fontSize: '14px' },
  instructionsContainer: { backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)', transition: 'all 0.3s ease' },
  instructionsCollapsed: { padding: '10px 16px', cursor: 'pointer' },
  instructionsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' },
  expandCollapseIcon: { color: '#666', fontSize: '12px' },
  instructionTitle: { marginTop: 0, marginBottom: '12px', fontSize: '16px', color: '#333' },
  instructionList: { marginLeft: '20px', paddingLeft: '0', lineHeight: '1.6' },
  instructionNote: { marginTop: '12px', color: '#666', fontStyle: 'italic', fontSize: '14px' },
  instructionActions: { marginTop: '16px', display: 'flex', justifyContent: 'flex-end' },
  instructionButton: { backgroundColor: '#4285f4', color: 'white', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' },
  testButton: { backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px', marginBottom: '10px' },
};

export default PushNotificationComponent;